import {useEffect, useState} from "react"
import DatalakeService from "../../../../services/datalake-service"
import {formatCpfCnpj} from "../../../utils/formatter/format-cpf-cnpj"
import {getCompanyOptionLabel} from "src/v2/utils/autocomplete/get-company-option-label"

let cachedPromise = null
let cachedResults = null

const service = new DatalakeService()

export const normalizeDoc = (val) => `${val || ""}`.replace(/\D/g, "")

export const defaultGetLabel = (option) => {
    if (!option) return ""
    if (typeof option === "string") return option
    const labelText = option?.label || option?.nome || option?.name || ""
    const doc = option?.value || option?.cpf_cnpj || option?.cpfCnpj || ""
    if (labelText) return labelText
    return doc
}

export const buildEmpresasAutoCompleteMeta = (options = [], loading = false) => {
    const getLabel = (option) => defaultGetLabel(option)
    const findOption = (opts, rawVal) => {
        const target = normalizeDoc(typeof rawVal === "string" ? rawVal : rawVal?.value || rawVal)
        return opts.find((opt) => normalizeDoc(opt.value) === target)
    }
    const isValid = (rawText) => {
        const raw = typeof rawText === "object" ? (rawText?.value || rawText?.label || "") : rawText
        const doc = normalizeDoc(raw)
        const rawLower = `${raw || ""}`.trim().toLowerCase()
        return options.some((opt) => {
            const optDoc = normalizeDoc(opt.value)
            const optLabel = `${opt.label || ""}`.toLowerCase()
            const docMatch = doc && optDoc === doc
            const labelMatch = rawLower && optLabel.includes(rawLower)
            return docMatch || labelMatch
        })
    }
    const filterOptions = (opts, params) => {
        const queryRaw = params?.inputValue || ""
        const queryLower = queryRaw.toLowerCase().trim()
        const queryDigits = normalizeDoc(queryRaw)
        if (!queryLower && !queryDigits) return []
        return (opts || []).filter((option) => {
            if (!option) return false
            const label = `${option.label || ""}`.toLowerCase()
            const valueDigits = normalizeDoc(option.value)
            const labelMatch = queryLower && label.includes(queryLower)
            const docMatch = queryDigits && valueDigits.includes(queryDigits)
            return labelMatch || docMatch
        })
    }

    return {
        options,
        loading,
        normalizeValue: normalizeDoc,
        getOptionLabel: getLabel,
        findOption,
        isValid,
        filterOptions,
        minCharsToFilter: 1,
        freeSolo: true,
        filterKeys: ["label", "value", "search"],
        normalizeFilter: (val) => {
            const s = `${val || ""}`
            const lower = s.toLowerCase()
            const digits = s.replace(/\D/g, "")
            return `${lower} ${digits}`.trim()
        },
        noOptionsText: (input) =>
            input ? `Não cadastrado ${input}` : "Nenhum resultado encontrado",
    }
}

export const invalidateEmpresasCache = () => {
    cachedPromise = null
    cachedResults = null
}

export const useEmpresasList = (enabled = true, reloadKey = 0) => {
    const [loading, setLoading] = useState(enabled && (!cachedResults || reloadKey > 0))
    const [options, setOptions] = useState(cachedResults || [])

    useEffect(() => {
        if (!enabled) {
            setLoading(false)
            return
        }

        let cancelled = false
        const forceReload = reloadKey > 0

        if (forceReload) {
            invalidateEmpresasCache()
        } else if (cachedResults) {
            setOptions(cachedResults)
            setLoading(false)
            return
        }

        const promise = cachedPromise || service.loadList()
        cachedPromise = promise
        setLoading(true)

        promise
            .then((data) => {
                const results = data?.body?.results || data?.results || []
                const mapped =
                    results?.map((item) => {
                        const docRaw = `${item?.CPFCNPJ || ""}`.trim()
                        const docFormatted = formatCpfCnpj(docRaw)
                        const label = getCompanyOptionLabel(item)
                        const searchFields = [item?.RazaoSocial, docRaw, docFormatted]
                            .filter(Boolean)
                            .join(" ")
                            .toLowerCase()
                        return {
                            label,
                            value: docRaw,
                            search: searchFields,
                            original: item,
                        }
                    }) || []

                cachedResults = mapped
                if (!cancelled) {
                    setOptions(mapped)
                }
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error("[useEmpresasList] fetch error", err)
                cachedResults = []
                if (!cancelled) {
                    setOptions([])
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [enabled, reloadKey])

    return {options, loading}
}
